import React, { useState, useEffect } from "react";
import useAuth from "../../Auth/useAuth";
import axios from "axios";
import "./UserManagement.css";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";

const listUsers = `${config.BASE_URL}/list-users`;
const addUserUrl = `${config.BASE_URL}/add-user`;
const updateUserUrl = `${config.BASE_URL}/update-user`;
const deleteUserUrl = `${config.BASE_URL}/delete-user`;

const UserManagement = () => {
  const { setActiveModule } = useAuth();

  const [userData, setUserData] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    profile: "",
    password: "",
    active: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    recordId: null,
    username: "",
  });

  // State for inline editing
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isPasswordVisible, setIsPasswordVisible] = useState({});

  const [isLoading, setIsLoading] = useState(true);

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Modal handler functions
  const showModal = (title, message, type = "info") => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    sessionStorage.setItem("actMod", "list-users");
    setActiveModule(sessionStorage.getItem("actMod"));
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(listUsers, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        });
        console.log("Fetched users:", response.data.users);
        if (response.data.success === false) {
          console.error("Error fetching users:", response.data.message);
          return;
        }
        setUserData(response.data.users || []);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, []);

  // Initialize filtered users
  useEffect(() => {
    setFilteredUsers(userData);
  }, [userData]);

  // Filter users based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredUsers(userData);
    } else {
      const filtered = userData.filter((user) =>
        user.username.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, userData]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      const response = await axios.post(addUserUrl, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("Response:", response);
      if (response.status === 201) {
        setUserData([...userData, response.data.user]);
        showModal("Success", "User added successfully!", "success");
        resetForm();
      } else {
        console.error("Error adding user:", response.data.message);
        showModal(
          "Error",
          `Error adding user: ${response.data.message}`,
          "error",
        );
      }
    } catch (error) {
      showModal("Error", `Error adding user: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Inline Edit Handlers
  const handleEditClick = (user) => {
    setEditingId(user._id);
    setEditFormData({
      username: user.username,
      profile: user.profile,
      password: user.password,
      active: user.active,
    });
  };

  const handleEditFormChange = (e, field) => {
    const { value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [field]: type === "checkbox" ? checked : value,
    });
  };

  const handleSaveEdit = async (userId) => {
    try {
      setIsLoading(true);
      const updatedData = {
        _id: userId,
        ...editFormData,
      };

      const response = await axios.put(updateUserUrl, updatedData, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200) {
        setUserData(
          userData.map((user) =>
            user._id === userId ? response.data.user : user,
          ),
        );
        showModal("Success", "User updated successfully!", "success");
        setEditingId(null);
        setEditFormData({});
      } else {
        console.error("Error updating user:", response.data.message);
        showModal(
          "Error",
          `Error updating user: ${response.data.message}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Error updating user:", error);
      showModal("Error", `Error updating user: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
    setIsPasswordVisible({});
  };

  const togglePasswordVisibility = (userId) => {
    setIsPasswordVisible({
      ...isPasswordVisible,
      [userId]: !isPasswordVisible[userId],
    });
  };

  const resetForm = () => {
    setFormData({
      username: "",
      profile: "",
      password: "",
      active: false,
    });
    setShowPassword(false);
  };

  const openDeleteModal = (userId, username) => {
    setDeleteModal({
      isOpen: true,
      recordId: userId,
      username: username,
    });
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      const response = await axios.delete(deleteUserUrl, {
        data: { _id: deleteModal.recordId },
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.status === 200) {
        console.log("User deleted successfully:", response.data);
        setUserData(
          userData.filter((user) => user._id !== deleteModal.recordId),
        );
        setDeleteModal({ isOpen: false, recordId: null, username: "" });
        showModal("Success", "User deleted successfully!", "success");
      } else {
        console.error("Error deleting user:", response.data.message);
        showModal(
          "Error",
          `Error deleting user: ${response.data.message}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      showModal("Error", `Error deleting user: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="user-management-container">
      <AlertModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
      
      {/* Add User Form (only for adding new users) */}
      <div className="userForm">
        <h2 className="userForm-title">Add New User</h2>
        <form onSubmit={handleAddUser}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleFormChange}
                required
                className="form-control"
                placeholder="Enter username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="profile">Profile</label>
              <select
                id="profile"
                name="profile"
                value={formData.profile}
                onChange={handleFormChange}
                className="form-control"
                required
              >
                <option value="">Select Profile</option>
                <option value="admin">Admin</option>
                <option value="driverAssociate">Driver Associate</option>
                <option value="loadingManager">Loading Manager</option>
                <option value="unloadingManager">Unloading Manager</option>
                <option value="accountant">Accountant</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  required
                  className="form-control"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* <div className="form-group">
              <label htmlFor="active">Status</label>
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleFormChange}
                  />
                  Active
                </label>
              </div>
            </div> */}

            <div className="form-group">
              <button type="submit" className="add-user-btn">
                Add User
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder="Search by username..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>

      {/* Table with Inline Edit */}
      <div className="table-wrapper">
        <div className="scrollable-table">
          <table id="userTable">
            <thead>
              <tr>
                <th>Username</th>
                <th>Profile</th>
                <th>Password</th>
                {/* <th>Status</th> */}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <div className="spinner-container">
                      <div className="spinner"></div>
                      <span>Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user._id} className={editingId === user._id ? "editing-row" : ""}>
                    <td>
                      {editingId === user._id ? (
                        <input
                          type="text"
                          value={editFormData.username}
                          onChange={(e) => handleEditFormChange(e, "username")}
                          className="inline-edit-input"
                        />
                      ) : (
                        user.username
                      )}
                    </td>
                    <td>
                      {editingId === user._id ? (
                        <select
                          value={editFormData.profile}
                          onChange={(e) => handleEditFormChange(e, "profile")}
                          className="inline-edit-select"
                        >
                          <option value="admin">Admin</option>
                          <option value="driverAssociate">Driver Associate</option>
                          <option value="loadingManager">Loading Manager</option>
                          <option value="unloadingManager">Unloading Manager</option>
                          <option value="accountant">Accountant</option>
                        </select>
                      ) : (
                        user.profile
                      )}
                    </td>
                    <td>
                      {editingId === user._id ? (
                        <div style={{ position: "relative" }}>
                          <input
                            type={isPasswordVisible[user._id] ? "text" : "password"}
                            value={editFormData.password}
                            onChange={(e) => handleEditFormChange(e, "password")}
                            className="inline-edit-input"
                            style={{ paddingRight: "35px" }}
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility(user._id)}
                            style={{
                              position: "absolute",
                              right: "6px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {isPasswordVisible[user._id] ? (
                              /* Hide password icon */
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                {/* <line x1="1" y1="1" x2="23" y2="23"></line> */}
                              </svg>
                            ) : (
                              /* Show password icon */
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            )}
                          </button>
                        </div>
                      ) : (
                        "•".repeat(8)
                      )}
                    </td>
                    {/* <td>
                      {editingId === user._id ? (
                        <div className="checkbox-group" style={{ justifyContent: "center" }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={editFormData.active}
                              onChange={(e) => handleEditFormChange(e, "active")}
                              className="inline-edit-checkbox"
                            />
                            Active
                          </label>
                        </div>
                      ) : (
                        <span className={`status-badge ${user.active ? "status-active" : "status-inactive"}`}>
                          {user.active ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td> */}
                    <td>
                      {editingId === user._id ? (
                        <div className="inline-edit-controls">
                          <button
                            className="save-btn"
                            onClick={() => handleSaveEdit(user._id)}
                          >
                            Save
                          </button>
                          <button
                            className="cancel-edit-btn"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="inline-edit-controls">
                          <button
                            className="edit-btn"
                            onClick={() => handleEditClick(user)}
                          >
                            Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => openDeleteModal(user._id, user.username)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "2rem" }}>
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="54"
                height="54"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                  fill="#ff4757"
                  d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z"
                />
              </svg>
            </div>
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this user:
              <span className="user-id-highlight"> {deleteModal.username}</span>
              ?
            </p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="delete-modal-buttons">
              <button
                className="modal-cancel-btn"
                onClick={() =>
                  setDeleteModal({
                    isOpen: false,
                    recordId: null,
                    username: "",
                  })
                }
              >
                Cancel
              </button>
              <button className="modal-confirm-delete-btn" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;